import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class StatusService {

  /**
   * Returns Bootstrap badge classes for statuses
   * Handles both general statuses and EVSE/Connector specific statuses
   * @param status - The status string to get badge class for
   * @returns Bootstrap badge class string
   */
  getStatusBadgeClass(status: string | null): string {
    if (!status) {
      return 'badge bg-secondary';
    }

    const normalized = this.normalizeStatus(status);

    // Handle Charger/Port/Connector/Network specific and general statuses
    switch (normalized) {
      // Success (green)
      case 'available':
      case 'online':
      case 'active':
      case 'accepted':
      case 'reachable':
      case 'operational':
        return 'badge bg-success';

      // Warning (yellow)
      case 'blocked':
      case 'reserved':
      case 'inmaintenance':
      case 'maintenance':
      case 'suspended':
      case 'suspendedev':
      case 'suspendedevse':
        return 'badge bg-warning';

      // Primary (blue)
      case 'charging':
      case 'inuse':
      case 'finishing':
      case 'preparing':
        return 'badge bg-primary';

      // Danger (red)
      case 'inoperative':
      case 'outoforder':
      case 'error':
      case 'rejected':
      case 'faulted':
      case 'needsservice':
      case 'unreachable':
        return 'badge bg-danger';

      // Info (cyan)
      case 'planned':
      case 'new':
        return 'badge bg-info';

      // Secondary (gray)
      case 'removed':
      case 'unknown':
      case 'offline':
      case 'inactive':
      case 'unavailable':
        return 'badge bg-secondary';

      default:
        // Check for fault/error in status (for statuses containing "fault"/"error" as substring)
        if (normalized.includes('fault') || normalized.includes('error')) {
          return 'badge bg-danger';
        }
        return 'badge bg-secondary';
    }
  }

  /**
   * Text color classes for Health tables (chargers use icons; meters/infra use colored labels).
   * Mirrors {@link getStatusBadgeClass} semantics without badge styling.
   */
  getStatusTextClass(status: string | null): string {
    if (!status) {
      return 'text-secondary';
    }

    const normalized = this.normalizeStatus(status);

    switch (normalized) {
      case 'available':
      case 'online':
      case 'active':
      case 'accepted':
      case 'reachable':
      case 'operational':
        return 'text-success';

      case 'blocked':
      case 'reserved':
      case 'inmaintenance':
      case 'maintenance':
      case 'suspended':
      case 'suspendedev':
      case 'suspendedevse':
        return 'text-warning';

      case 'charging':
      case 'inuse':
      case 'finishing':
      case 'preparing':
        return 'text-primary';

      case 'inoperative':
      case 'outoforder':
      case 'error':
      case 'rejected':
      case 'faulted':
      case 'needsservice':
      case 'unreachable':
        return 'text-danger';

      case 'planned':
      case 'new':
        return 'text-info';

      case 'removed':
      case 'unknown':
      case 'offline':
      case 'inactive':
      case 'unavailable':
        return 'text-secondary';

      default:
        if (normalized.includes('fault') || normalized.includes('error')) {
          return 'text-danger';
        }
        return 'text-secondary';
    }
  }

  /** Health → Meters tab: integration status (Active / Offline / New / Removed). */
  getHealthIntegrationStatusTextClass(status: string | null): string {
    if (!status) {
      return 'text-secondary';
    }
    const normalized = this.normalizeStatus(status);
    switch (normalized) {
      case 'active':
        return 'text-success';
      case 'offline':
        return 'text-warning';
      case 'new':
        return 'text-info';
      case 'removed':
        return 'text-secondary';
      default:
        return 'text-secondary';
    }
  }

  /** Charger integration (Idle) badge — neutral when not receiving session data. */
  getChargerIntegrationStatusBadgeClass(status: string | null): string {
    if (!status) {
      return 'badge bg-secondary';
    }
    const normalized = this.normalizeChargerIntegrationStatus(status);
    switch (normalized) {
      case 'active':
        return 'badge bg-primary';
      case 'idle':
        return 'badge bg-secondary';
      case 'new':
        return 'badge bg-info';
      case 'removed':
        return 'badge bg-secondary';
      default:
        return 'badge bg-secondary';
    }
  }

  /** Meter integration (Offline) badge — warning when not receiving telemetry. */
  getMeterIntegrationStatusBadgeClass(status: string | null): string {
    if (!status) {
      return 'badge bg-secondary';
    }
    const normalized = this.normalizeStatus(status);
    switch (normalized) {
      case 'active':
        return 'badge bg-primary';
      case 'offline':
        return 'badge bg-warning text-dark';
      case 'new':
        return 'badge bg-info';
      case 'removed':
        return 'badge bg-secondary';
      default:
        return 'badge bg-secondary';
    }
  }

  /**
   * @deprecated Use {@link getChargerIntegrationStatusBadgeClass} or
   * {@link getMeterIntegrationStatusBadgeClass}.
   */
  getIntegrationStatusBadgeClass(status: string | null): string {
    return this.getChargerIntegrationStatusBadgeClass(status);
  }

  /** Human-readable labels for charger integration (session data) status. */
  getChargerIntegrationStatusText(status: string | null): string {
    if (!status) {
      return 'Unknown';
    }
    const normalized = this.normalizeChargerIntegrationStatus(status);
    switch (normalized) {
      case 'active':
        return 'Receiving Data';
      case 'idle':
        return 'Idle';
      case 'new':
        return 'New';
      case 'removed':
        return 'Removed';
      default:
        return status;
    }
  }

  /** Human-readable labels for meter integration status. */
  getMeterIntegrationStatusText(status: string | null): string {
    if (!status) {
      return 'Unknown';
    }
    const normalized = this.normalizeStatus(status);
    switch (normalized) {
      case 'active':
        return 'Receiving Data';
      case 'offline':
        return 'Offline';
      case 'new':
        return 'New';
      case 'removed':
        return 'Removed';
      default:
        return status;
    }
  }

  /** @deprecated Use {@link getChargerIntegrationStatusText} for chargers. */
  getIntegrationStatusText(status: string | null): string {
    return this.getChargerIntegrationStatusText(status);
  }

  private normalizeChargerIntegrationStatus(status: string): string {
    return this.normalizeStatus(status);
  }

  private normalizeStatus(status: string): string {
    return status.toLowerCase().trim().replace(/[\s_-]/g, '');
  }
}
